/**
 * TxLoadBar — Shared compact toolbar for loading transactions + stats display.
 *
 * Used by both Transfers tab and Counterparties tab.
 * Row 1: stats (tx count, in/out, canvas count) + inline "Load N" button
 * Row 1.5 (expandable): date range loader
 */

import React from 'react';
import { ChevronDown, Calendar, Loader2, Filter } from 'lucide-react';
import { useFilterStore } from '../../stores/useFilterStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../utils/cn';
import { loadedSummary, partialReason } from '../../utils/txCoverage';

const COUNT_PRESETS = [10, 20, 50, 100, 200, 500, 1000] as const;

/** Quick windows officers commonly need when narrowing transfer history. */
const RANGE_PRESETS: { label: string; days: number }[] = [
    { label: '7d', days: 7 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
];

function toDateInputValue(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function applyPresetDays(days: number): { start: string; end: string } {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    return { start: toDateInputValue(start), end: toDateInputValue(end) };
}

export interface EnrichmentStatus {
    status: 'idle' | 'running' | 'complete' | 'error';
    total: number;
    found: number;
    currentProvider: string | null;
    message?: string;
}

export interface TxLoadBarProps {
    /** Total loaded transactions */
    txCount: number;
    /** Incoming tx count */
    incomingCount: number;
    /** Outgoing tx count */
    outgoingCount: number;
    /** How many txs are on canvas */
    canvasCount: number;
    /** Total on-chain txs when known (the real count from a load, else the triage count); 0 = unknown */
    approxTotal: number;
    /** From the last load: false = the fetcher reported the history exhausted, true = more may exist, undefined = not known */
    hasMore?: boolean;
    /** The last load was cut by a cap (hub clamp or page limit) */
    truncated?: boolean;
    /** A leg of the last load failed, so the rows may be a prefix of the history */
    incomplete?: boolean;
    /** Data source indicator (arkham, live, cached) */
    dataSource?: string;
    /** Whether all txs in date range have been loaded */
    dateRangeAllLoaded?: boolean;

    // Load by count
    onLoadByCount?: (count: number) => Promise<void>;
    // Load by date range
    onLoadByDateRange?: (start: string, end: string, onProgress: (loaded: number, batch: number) => void) => Promise<void>;
    cancelDateRangeRef?: React.MutableRefObject<boolean>;
    // Date state (controlled by parent)
    txStartDate?: string;
    txEndDate?: string;
    setTxStartDate?: (v: string) => void;
    setTxEndDate?: (v: string) => void;

    /** Background entity enrichment status */
    enrichmentStatus?: EnrichmentStatus;
}

export const TxLoadBar: React.FC<TxLoadBarProps> = ({
    txCount,
    incomingCount,
    outgoingCount,
    canvasCount,
    approxTotal,
    hasMore,
    truncated,
    incomplete,
    dataSource,
    dateRangeAllLoaded,
    onLoadByCount,
    onLoadByDateRange,
    cancelDateRangeRef,
    txStartDate = '',
    txEndDate = '',
    setTxStartDate,
    setTxEndDate,
    enrichmentStatus,
}) => {
    const [countPreset, setCountPreset] = React.useState(100);
    const [countLoading, setCountLoading] = React.useState(false);
    const [showDateRange, setShowDateRange] = React.useState(false);
    const [dateLoading, setDateLoading] = React.useState(false);
    const [batchProgress, setBatchProgress] = React.useState({ loaded: 0, batch: 0 });

    // CANVAS_PLACEMENT_LANGUAGE / Inspector dust toggle. Default ON —
    // sub-cent transactions are overwhelmingly noise (USDT-spam, BFS
    // path-context, fee-tier dust at 0.000001 TRX). When OFF, the rows
    // still render but with reduced visual weight (deprioritised) so
    // they don't compete with material transactions.
    const hideDust = useFilterStore(s => s.hideDust);
    const setHideDust = useFilterStore(s => s.setHideDust);

    const handleLoadCount = async () => {
        if (!onLoadByCount) return;
        setCountLoading(true);
        try { await onLoadByCount(countPreset); } finally { setCountLoading(false); }
    };

    const handleLoadDateRange = async () => {
        if (!onLoadByDateRange || !txStartDate || !txEndDate) return;
        setDateLoading(true);
        setBatchProgress({ loaded: 0, batch: 0 });
        if (cancelDateRangeRef) cancelDateRangeRef.current = false;
        try {
            await onLoadByDateRange(txStartDate, txEndDate, (loaded, batch) => setBatchProgress({ loaded, batch }));
        } finally { setDateLoading(false); }
    };

    return (
        <div className="space-y-0">
            {/* Row 1: Stats left + Load right */}
            <div className="flex items-center justify-between gap-2 px-1 py-1">
                {/* Stats */}
                <div className="flex items-center gap-1.5 text-[11px] min-w-0">
                    <span className="font-semibold text-foreground" data-testid="tx-load-summary">{loadedSummary(txCount, approxTotal, hasMore)}{approxTotal > txCount ? ' txs' : ''}</span>
                    {(truncated || incomplete) && (
                        <span
                            data-testid="tx-partial-chip"
                            title={partialReason({ truncated, incomplete })}
                            className="text-[10px] px-1 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400"
                        >
                            partial
                        </span>
                    )}
                    <span className="text-green-600 dark:text-green-400">&#8595;{incomingCount}</span>
                    <span className="text-red-600 dark:text-red-400">&#8593;{outgoingCount}</span>
                    {canvasCount > 0 && <span className="text-primary">{canvasCount} on canvas</span>}
                    {dataSource && (
                        <span className={`text-[10px] px-1 rounded ${
                            dataSource === 'arkham' ? 'bg-primary/15 text-primary' :
                            dataSource === 'live' ? 'bg-green-500/15 text-green-500' :
                            'bg-muted text-muted-foreground'
                        }`}>
                            {dataSource === 'arkham' ? 'Arkham' : dataSource === 'live' ? 'Direct' : 'Cached'}
                        </span>
                    )}
                    {dateRangeAllLoaded && <span className="text-primary">all loaded</span>}
                </div>

                {/* Dust toggle — pill-style. ON state hides; OFF shows-but-deprioritises. */}
                <button
                    onClick={() => setHideDust(!hideDust)}
                    className={`shrink-0 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border transition-colors ${
                        hideDust
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-input text-muted-foreground hover:text-foreground'
                    }`}
                    title={
                        hideDust
                            ? 'Hiding dust (<$0.01) — click to show with reduced weight'
                            : 'Showing dust (<$0.01) deprioritised — click to hide'
                    }
                >
                    <Filter className="w-3 h-3" />
                    <span>{hideDust ? 'Hide dust' : 'Show dust'}</span>
                </button>

                {/* Load controls */}
                {onLoadByCount && (
                    <div className="flex items-center gap-1 shrink-0">
                        <div className="relative">
                            <select
                                value={countPreset}
                                onChange={e => setCountPreset(Number(e.target.value))}
                                className="text-[10px] bg-background border border-input rounded pl-1.5 pr-4 py-0.5 appearance-none font-mono focus:ring-1 focus:ring-primary/50"
                            >
                                {COUNT_PRESETS.map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <ChevronDown className="w-2.5 h-2.5 absolute right-0.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        </div>
                        <button
                            onClick={handleLoadCount}
                            disabled={countLoading}
                            className="text-[10px] px-2 py-0.5 font-medium text-primary border border-blue-300 dark:border-blue-800 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 transition-colors"
                        >
                            {countLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Load'}
                        </button>
                        {onLoadByDateRange && (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                onClick={() => setShowDateRange(!showDateRange)}
                                className={cn(
                                    'h-6 w-6 shrink-0',
                                    showDateRange && 'border-primary bg-primary/10 text-primary',
                                )}
                                title="Load by date range"
                                aria-expanded={showDateRange}
                                aria-label="Load by date range"
                            >
                                <Calendar className="h-3 w-3" />
                            </Button>
                        )}
                    </div>
                )}
            </div>

            {/* Row 1.5: Date range (expandable) */}
            {showDateRange && !dateLoading && (
                <div className="mx-1 mt-1 rounded-lg border border-border bg-card/60 p-2.5 shadow-sm shadow-black/5">
                    <div className="mb-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                            <p className="text-[11px] font-medium text-foreground">Load by date range</p>
                            <p className="text-[10px] text-muted-foreground">
                                Narrow transfers to a window before fetching.
                            </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                            {RANGE_PRESETS.map(({ label, days }) => (
                                <Button
                                    key={label}
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-6 px-1.5 text-[10px]"
                                    onClick={() => {
                                        const { start, end } = applyPresetDays(days);
                                        setTxStartDate?.(start);
                                        setTxEndDate?.(end);
                                    }}
                                >
                                    {label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <label className="min-w-0 space-y-1">
                            <span className="text-[10px] font-medium text-muted-foreground">From</span>
                            <Input
                                type="date"
                                value={txStartDate}
                                onChange={e => setTxStartDate?.(e.target.value)}
                                className="h-8 px-2 text-[11px]"
                            />
                        </label>
                        <label className="min-w-0 space-y-1">
                            <span className="text-[10px] font-medium text-muted-foreground">To</span>
                            <Input
                                type="date"
                                value={txEndDate}
                                onChange={e => setTxEndDate?.(e.target.value)}
                                className="h-8 px-2 text-[11px]"
                            />
                        </label>
                    </div>

                    <div className="mt-2 flex items-center justify-between gap-2">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-[11px]"
                            disabled={!txStartDate && !txEndDate}
                            onClick={() => {
                                setTxStartDate?.('');
                                setTxEndDate?.('');
                            }}
                        >
                            Clear
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            className="h-7 px-3 text-[11px]"
                            disabled={!txStartDate || !txEndDate}
                            onClick={handleLoadDateRange}
                        >
                            Apply
                        </Button>
                    </div>
                </div>
            )}

            {/* Date range progress */}
            {showDateRange && dateLoading && (
                <div className="mx-1 mt-1 space-y-1.5 rounded-lg border border-border bg-card/60 p-2.5 shadow-sm shadow-black/5">
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-primary" />
                        <span>Fetching transfers in range…</span>
                    </div>
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary/50">
                        <div
                            className="h-full rounded-full bg-primary transition-all duration-300"
                            style={{ width: approxTotal ? `${Math.min(100, (batchProgress.loaded / approxTotal) * 100)}%` : '50%' }}
                        />
                    </div>
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                        <span>{batchProgress.loaded.toLocaleString()} txs (batch {batchProgress.batch})</span>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-[10px] text-destructive hover:text-destructive"
                            onClick={() => { if (cancelDateRangeRef) cancelDateRangeRef.current = true; }}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            {/* Entity enrichment status bar */}
            {enrichmentStatus && enrichmentStatus.status !== 'idle' && (
                <div className="flex items-center gap-1.5 px-1 py-0.5 text-[10px] bg-amber-500/10 border-t border-amber-500/20">
                    {enrichmentStatus.status === 'running' && (
                        <>
                            <Loader2 className="w-3 h-3 animate-spin text-amber-500 shrink-0" />
                            <span className="text-amber-600 dark:text-amber-400">
                                Enriching entities{enrichmentStatus.currentProvider ? ` (${enrichmentStatus.currentProvider})` : ''}...
                                {enrichmentStatus.found > 0 && ` ${enrichmentStatus.found} found`}
                            </span>
                        </>
                    )}
                    {enrichmentStatus.status === 'complete' && enrichmentStatus.found > 0 && (
                        <span className="text-green-600 dark:text-green-400">
                            {enrichmentStatus.found} new entities identified — re-run algorithm for updated results
                        </span>
                    )}
                    {enrichmentStatus.status === 'complete' && enrichmentStatus.found === 0 && (
                        <span className="text-muted-foreground">
                            No additional entities found
                        </span>
                    )}
                    {enrichmentStatus.status === 'error' && (
                        <span className="text-red-400">
                            Entity enrichment failed
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};
